import {
  compatRouter,
  type Query,
  type TransitionOptions,
  type Url,
} from "@src/shims/next/router-runtime";
import { useMatch, useRouterState } from "@tanstack/react-router";

// Next query values are strings; TanStack JSON-parses search values,
// so objects go back via JSON.stringify (e.g. filters param)
function searchToQuery(search: Record<string, unknown>): Query {
  const query: Query = {};

  Object.entries(search).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (typeof value === "string") {
      query[key] = value;
      return;
    }

    if (Array.isArray(value)) {
      query[key] = value.map((item) => String(item));
      return;
    }

    if (typeof value === "object") {
      query[key] = JSON.stringify(value);
      return;
    }

    query[key] = String(value);
  });

  return query;
}

type NextRouterCompat = {
  pathname: string;
  route: string;
  asPath: string;
  query: Query;
  isReady: boolean;
  isFallback: false;
  isLocaleDomain: false;
  isPreview: false;
  basePath: "";
  push: (url: Url, as?: Url, options?: TransitionOptions) => Promise<boolean>;
  replace: (
    url: Url,
    as?: Url,
    options?: TransitionOptions,
  ) => Promise<boolean>;
  back: () => void;
  reload: () => void;
  prefetch: () => Promise<void>;
  events: typeof compatRouter.events;
};

export function useRouter(): NextRouterCompat {
  const location = useRouterState({ select: (state) => state.location });
  const search = useMatch({
    strict: false,
    select: (match) => match.search as Record<string, unknown>,
  });
  const asPath = `${location.pathname}${location.searchStr}${location.hash}`;

  return {
    pathname: location.pathname,
    route: location.pathname,
    asPath,
    query: searchToQuery(search),
    isReady: true,
    isFallback: false,
    isLocaleDomain: false,
    isPreview: false,
    basePath: "",
    push: compatRouter.push,
    replace: compatRouter.replace,
    back: compatRouter.back,
    reload: compatRouter.reload,
    prefetch: compatRouter.prefetch,
    events: compatRouter.events,
  };
}

export default compatRouter;
